import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import { get } from 'lodash'
import { compose } from 'recompose'

import { Select, Checkbox } from 'components/fields'
import { Button, Note, Title, FormDebug } from 'components/misc'

import { awardUnit } from 'constants/wizards'
import { wizard, meta } from 'helpers/decorators'
import { apiCommon, apiAwardUnits, apiOrganizations, apiConfiguration } from 'helpers/decorators/api'
import { generalSelectMap } from 'redux/selectors/misc'
import validate from './award-unit-select.validation'
import { CONFIGURATION_KEYS } from '../../../../constants/enums'

const enhance = compose(
  apiCommon({ publicCountries: true, rejectEmpty: true }),
  apiAwardUnits(),
  apiConfiguration(),
  apiOrganizations(),
  wizard({
    form: 'awardUnitRegistration',
    multipage: true,
    destroyOnUnmount: false,
    disableHook: true,
    validate
  }),
  meta({
    title: 'Select Award Unit',
    intlKey: 'wizard.select_award_unit'
  })
)

class AwardUnitSelect extends Component {
  static propTypes = {
    change: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    checkOperatingAuthorities: PropTypes.func.isRequired,
    changeWizardActive: PropTypes.func.isRequired,
    changeWizardNext: PropTypes.func.isRequired,
    onFetchGlobalOperatingPartners: PropTypes.func.isRequired,
    onFetchSubGlobalOperatingPartners: PropTypes.func.isRequired,
    values: PropTypes.object,
    errors: PropTypes.object,
    common: PropTypes.object,
    gopList: PropTypes.array,
    onGetConfigurationValue: PropTypes.func.isRequired
  }

  state = {
    operatingAuthorities: []
  }

  componentDidUpdate = prevProps => {
    const { steps, rootPath } = awardUnit

    const isIacRegistration = get(this.props, 'values.isIacRegistration')

    if (get(prevProps, 'values.isIacRegistration') !== isIacRegistration) {
      this.props.changeWizardActive(
        steps.authorisingContact.id,
        !isIacRegistration
      )

      if (isIacRegistration) {
        this.props.changeWizardNext(
          steps.details.id,
          rootPath + steps.leader.path
        )
        return
      }
      this.props.changeWizardNext(
        steps.details.id,
        rootPath + steps.authorisingContact.path
      )
    }

  }

  loadConfig = async() => {
    await this.fetchIsGOPEnabled(1)
  }



  fetchGlobalOperatingPartner = () => {
    return this.props.onFetchGlobalOperatingPartners().then(res => {
      this.props.change('helpers.globalOperatingPartners', get(res, 'body'))
      return res
    })
  }

  fetchSubGlobalOperatingPartner = gopId => {
    return this.props.onFetchSubGlobalOperatingPartners(gopId).then(res => {
      this.props.change('helpers.subGlobalOperatingPartners', get(res, 'body'))
      return res
    })
  }


  fetchIsGOPEnabled = orgId => {
    return this.props.onGetConfigurationValue(CONFIGURATION_KEYS.isGOPEnabled.id, orgId, true).then(res => {
      this.props.change('helpers.isGOPEnabled', get(res, 'body.booleanValue', false) )
      return res
    })
  }

  handleChangeCountry = ({ value }) => {
    if (!value) {
      this.props.change('helpers', {})
      return
    }

    const countries = get(this.props.common, 'countries.data')

    const selectedCountry = countries.filter(item => item.id === value)[0]

    const isPresentOa = get(selectedCountry, 'isPresentOa', false)


    if (isPresentOa) {
      this.handleCheckOperatingAuthority({
        countryId: value,
        name: ''
      })
    }

    this.props.change('helpers', {})
    this.props.change('helpers.selectedCountry', selectedCountry)
    this.props.change('operatingAuthorityId', '')
    this.props.change('subOperatingAuthorityId', '')
    this.props.change('naoId', selectedCountry.naoId)
    this.props.change('icId', selectedCountry.icId)
    const isIacRegistration = get(this.props, 'values.isIacRegistration')

    this.loadConfig()

    isIacRegistration && this.fetchGlobalOperatingPartner()
  }

  handleChangeIac = () => {
    this.fetchGlobalOperatingPartner()
    this.props.change('operatingAuthorityId', '')
    this.props.change('subOperatingAuthorityId', '')
    this.props.change('helpers.subOAOptions', [])
  }

  handleCheckNoPartOfGOP = () => {
    this.props.change('globalOperatingPartnerId', '')
    this.props.change('subGlobalOperatingPartnerId', '')
  }

  handleCheckOperatingAuthority = params => {
    get(params, 'name') &&
      this.props.change(
        'helpers.lastSearchedOperatingAuthority',
        get(params, 'name')
      )

    return this.props.checkOperatingAuthorities(params).then(res => {
      this.props.change('helpers.operatingAuthorities', get(res, 'body'))
      return res
    })
  }

  handleChangeOA = ({ value } = {}) => {
    const { values } = this.props
    const operatingAuthorities = get(values, 'helpers.operatingAuthorities', [])

    if (!value) {
      this.props.change('helpers.selectedOa', {})
    }

    if (operatingAuthorities.length === 0) {
      return
    }

    const selectedOa = operatingAuthorities.filter(item => item.id === value)[0]

    this.props.change('helpers.selectedOa', selectedOa)
  }

  handleChangeGOP = async(item) => {
    const gopId = get(item, 'value')

    await gopId && this.fetchSubGlobalOperatingPartner(gopId)
    this.props.change('helpers.selectedGOP', gopId)
  }

  handleChangeSGOP = item => {
    const sgopId = get(item, 'value')

    this.props.change('helpers.selectedSGOP', sgopId)
  }

  render() {
    const { handleSubmit, onSubmit, values, errors, common } = this.props

    const countryId = get(values, 'countryId')

    const isIac = get(values, 'isIacRegistration')

    const lastSearched = get(values, 'helpers.lastSearchedOperatingAuthority')

    const isPresentSoa =
      get(values, 'helpers.selectedOa.subOperatingAuthorities', []).length > 0

    const isPresentIc = get(
      values,
      'helpers.selectedCountry.isPresentIc',
      false
    )
    const isPresentOa = get(
      values,
      'helpers.selectedCountry.isPresentOa',
      false
    )
    const isPresentNao = get(
      values,
      'helpers.selectedCountry.isPresentNao',
      false
    )
    const isGOPEnabled = false
/*    const isGOPEnabled = get(
      values,
      'helpers.isGOPEnabled',
      false
    )*/

    const mustBeChecked = !isPresentNao && !isPresentOa && isPresentIc

    const isIacRegistration = get(this.props, 'values.isIacRegistration')

    const disableGOPComboBox = get(this.props, 'values.noPartOfGOP')

    const gopList = get(values, 'helpers.globalOperatingPartners', [])

    const sgopList = get(values, 'helpers.subGlobalOperatingPartners', [])

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Title h1>
          <FormattedMessage
            id="moduleTitle.welcome_to_the_orb"
            defaultMessage="Welcome to the Online Record Book"
          />
        </Title>

        <Note lighter margin="0 0 35px 0">
          <FormattedMessage
            id="label.you_are_now_registering_as_award_unit"
            defaultMessage="You are now registering as an Award Unit"
          />
        </Note>

        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-4">
            <Select
              label="Select country"
              name="countryId"
              intlKey="label.select_country"
              options={get(common, 'countries.list')}
              onSelectChange={this.handleChangeCountry}
              searchable
            />
          </div>
        </div>

        {isPresentIc &&
          <Checkbox
            name="isIacRegistration"
            onChange={this.handleChangeIac}
            intlKey="label.i_want_to_register_my_award"
            label="I want to register my Award Unit as Independent Award Centre"
          />}


        {isIacRegistration && isPresentIc && isGOPEnabled &&
        <div className="row">
          <div className="col-xs-12 col-sm-4">
            <Select
              key={countryId}
              label="Select your Global Operating Partner"
              intlKey="label.select_your_global_operating_partner"
              name="globalOperatingPartnerId"
              disabled={disableGOPComboBox}
              options={generalSelectMap(gopList)}
              onSelectChange={item => this.handleChangeGOP(item)}
            />

          </div>
        </div>}

        {isIacRegistration && isPresentIc && isGOPEnabled &&
        <div className="row">
          <div className="col-xs-12 col-sm-4">
            <Select
              key={countryId}
              label="Select your Sub-Global Operating Partner"
              intlKey="label.select_your_sub_global_operating_partner"
              name="subGlobalOperatingPartnerId"
              disabled={disableGOPComboBox || (sgopList && sgopList.length === 0)}
              options={generalSelectMap(sgopList)}
              onSelectChange={item => this.handleChangeSGOP(item)}
            />

            <Checkbox
              name="noPartOfGOP"
              onChange={this.handleCheckNoPartOfGOP}
              intlKey="label.no_part_of_gop_or_not_sure"
              label="No part of GOP or not sure"
            />
          </div>
        </div>}


        {!isIac &&
          isPresentOa &&
          <div className="row">
            <div className="col-xs-12 col-sm-4">
              <Select
                key={countryId}
                label="Select your Operating Authority"
                intlKey="label.select_your_operating_authority"
                name="operatingAuthorityId"
                disabled={!countryId || !values}
                options={generalSelectMap(
                  get(values, 'helpers.operatingAuthorities', [])
                )}
                onSelectChange={this.handleChangeOA}
                autoloadValue={lastSearched}
              />
            </div>
          </div>}

        {isPresentSoa &&
          <div className="row">
            <div className="col-xs-12 col-sm-4">
              <Select
                label="Select sub Operating Authority"
                name="subOperatingAuthorityId"
                intlKey="label.select_sub_operating_authority"
                options={generalSelectMap(
                  get(values, 'helpers.selectedOa.subOperatingAuthorities', [])
                )}
              />
            </div>
          </div>}

        <Note margin="5px 0 25px" lighter>
          <FormattedHTMLMessage
            id="label.not_sure_about_your_auth"
            defaultMessage="Not sure about your Award Authority? Contact us on"
          />
          {' '}
        </Note>

        <FormDebug values={values} errors={errors} />

        <Button type="submit" disabled={mustBeChecked && !isIac}>
          <FormattedMessage id="button.continue" defaultMessage="Continue" />
        </Button>
      </form>
    )
  }
}

export default enhance(AwardUnitSelect)
